import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import BasicButton from "../basics/BasicButton";
import { useGetMyInvitesQuery } from "../../services/rtkApi/invitesApi";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #c9febe;
  align-items: center;
  padding: 15px;
  min-height: 100vh;
`;

const Content = styled.div`
  width: 100%;
  max-width: 980px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const StatCard = styled.div`
  background: #f4fff0;
  border: 1px solid #bdd5b9;
  border-radius: 8px;
  padding: 12px;
`;

const StatLabel = styled.div`
  color: #486048;
  font-size: 13px;
`;

const StatValue = styled.div`
  color: #1f341f;
  font-size: 24px;
  font-weight: 700;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #f8fff6;
  border: 1px solid #bdd5b9;

  th,
  td {
    text-align: left;
    padding: 10px;
    border-bottom: 1px solid #d7e7d4;
    font-size: 14px;
  }
`;

const Message = styled.p`
  color: #3a503a;
`;

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "n/a";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "n/a";
  }
  return date.toLocaleString();
};

const InviteStatsPage: React.FC = () => {
  const { data, isLoading, isError } = useGetMyInvitesQuery();
  const invites = data?.invites || [];

  const stats = {
    totalGenerated: invites.length,
    totalUsed: invites.filter((invite) => invite.status === "used").length,
    totalActive: invites.filter((invite) => invite.status === "active").length,
    totalExpired: invites.filter((invite) => invite.status === "expired").length,
    remainingQuota: data?.remainingQuota ?? 0,
  };

  return (
    <PageContainer className="inv-page">
      <style>{`
        @media (max-width: 700px) {
          .inv-page { padding: 12px 8px !important; }
          .inv-row { flex-wrap: wrap !important; gap: 8px !important; }
          .inv-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .inv-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
          .inv-grid-1 { grid-template-columns: 1fr !important; }
          .inv-card-grid { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important; }
          .inv-textarea-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; min-height: 120px; }
        }
      `}</style>
      <Content>
        <Header className="inv-row">
          <h2 style={{ margin: 0 }}>Invite Stats</h2>
          <Link to="/user-stats">
            <BasicButton text="Back to User Stats" />
          </Link>
        </Header>

        <CardGrid className="inv-card-grid">
          <StatCard>
            <StatLabel>Total generated</StatLabel>
            <StatValue>{stats.totalGenerated}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Total invited (used)</StatLabel>
            <StatValue>{stats.totalUsed}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Active invites</StatLabel>
            <StatValue>{stats.totalActive}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Remaining quota</StatLabel>
            <StatValue>{stats.remainingQuota}</StatValue>
          </StatCard>
        </CardGrid>

        {isLoading ? <Message>Loading invites...</Message> : null}
        {isError ? <Message>Failed to load invite stats.</Message> : null}

        {!isLoading && !isError && invites.length === 0 ? (
          <Message>No invites generated yet.</Message>
        ) : null}

        {!isLoading && !isError && invites.length > 0 ? (
          <div className="inv-table-wrap">
            <Table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Used by</th>
                  <th>Created at</th>
                  <th>Expires at</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite._id}>
                    <td>{invite.code}</td>
                    <td>{invite.status}</td>
                    <td>{invite.usedBy || "n/a"}</td>
                    <td>{formatDate(invite.createdAt)}</td>
                    <td>{formatDate(invite.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Content>
    </PageContainer>
  );
};

export default InviteStatsPage;
